<template>
  <div>
    <base-header class="pb-4">
      <div class="row align-items-center py-4">
        <div class="col-lg-8 col-7">
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <ol class="breadcrumb breadcrumb-links breadcrumb-dark">
              <li class="breadcrumb-item">
                <router-link to="/partes"><i class="fas fa-home"></i></router-link>
              </li>
              <li class="breadcrumb-item active">Detalle parte</li>
            </ol>
          </nav>
          <h6 class="h2 text-white d-inline-block mb-0">
            PARTE Nº {{ parteNumero }}
          </h6>
          <p class="text-white mb-0 mt-1 small opacity-8">{{ titulo }}</p>
        </div>
        <div class="col-lg-4 col-5 text-right">
          <base-button size="sm" type="neutral" @click="goBack">
            <i class="fas fa-arrow-left mr-2"></i>Volver
          </base-button>
          <base-button
            size="sm"
            type="primary"
            class="ml-2"
            @click="loadDetail(true)"
            :loading="loading"
          >
            <i class="fas fa-cloud-download-alt mr-2"></i>Refrescar SAT
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--4" v-loading="loading">
      <div v-if="error" class="alert alert-danger">{{ error }}</div>

      <template v-if="detail">
        <div class="row">
          <div class="col-12">
            <card>
              <div class="p-3">
                <h4 class="mb-1">{{ titulo }}</h4>
                <p class="text-muted mb-3">{{ displayField(parte.descripcion, 'Sin descripción') }}</p>
                <p v-if="displayField(parte.localizacion, null)" class="small text-muted mb-3">
                  <i class="fas fa-map-marker-alt mr-1"></i>{{ displayField(parte.localizacion) }}
                </p>

                <div class="row">
                  <div class="col-md-4">
                    <p><strong>Cliente:</strong> {{ displayField(parte.cliente) }}</p>
                    <p>
                      <strong>Estado SAT:</strong>
                      <span class="text-success">{{ displayField(parte.estadoSat) }}</span>
                    </p>
                    <p><strong>Asignado a:</strong> {{ displayField(parte.asignadoA || parte.empleado) }}</p>
                    <p>
                      <strong>Tarea facturación:</strong>
                      {{ displayField(parte.tareaFacturacion) }}
                    </p>
                  </div>
                  <div class="col-md-4">
                    <p><strong>Contacto:</strong> {{ displayField(parte.contacto) }}</p>
                    <p><strong>Fecha inicio:</strong> {{ formatDate(parte.fechaInicio) }}</p>
                    <p><strong>Tipo:</strong> {{ displayField(parte.tipoParte, 'Partes') }}</p>
                    <p><strong>Estado ERP:</strong> {{ displayField(parte.estadoErp) }}</p>
                    <p><strong>Proyecto:</strong> {{ displayField(parte.proyecto) }}</p>
                  </div>
                  <div class="col-md-4">
                    <p><strong>Planta:</strong> {{ displayField(parte.planta) }}</p>
                    <p><strong>Fecha fin:</strong> {{ formatDate(parte.fechaFin) }}</p>
                    <p><strong>Creado:</strong> {{ formatDate(parte.creado) }}</p>
                    <p><strong>Id. Parte SAT:</strong> {{ displayField(parte.idParte) }}</p>
                    <p v-if="parte.numParte && parte.numParte !== parte.idParte">
                      <strong>Nº Parte SAT:</strong> {{ displayField(parte.numParte) }}
                    </p>
                    <p><strong>Oferta:</strong> {{ displayField(parte.oferta) }}</p>
                  </div>
                </div>

                <router-link
                  v-if="detail.albaran"
                  :to="`/albaran-management/view-albaran/${detail.albaran.id}`"
                  class="btn btn-sm btn-outline-primary"
                >
                  Ver albarán {{ detail.albaran.numero }}
                </router-link>
              </div>
            </card>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-lg-6">
            <card>
              <div class="p-3">
                <h5 class="mb-3"><i class="fas fa-box mr-2 text-primary"></i>Material Albarán Abel</h5>
                <div v-if="!lineasMaterial.length" class="alert alert-info mb-0">
                  Sin material registrado en el albarán Abel.
                </div>
                <div v-else class="table-responsive">
                  <table class="table table-sm table-hover mb-0">
                    <thead class="thead-light">
                      <tr>
                        <th>Tipo</th>
                        <th>Nombre</th>
                        <th class="text-center">Cant.</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="linea in lineasMaterial" :key="linea.id">
                        <td>
                          <span class="badge text-white" :class="getTipoBadgeClass(linea.tipo)">
                            {{ getTipoLabel(linea.tipo) }}
                          </span>
                        </td>
                        <td>
                          <strong>{{ linea.nombre }}</strong>
                          <div v-if="linea.numero_serie || linea.qr" class="small text-muted">
                            {{ linea.numero_serie || linea.qr }}
                          </div>
                        </td>
                        <td class="text-center">{{ linea.cantidad }}</td>
                        <td>
                          <span class="badge" :class="getEstadoBadgeClass(linea.estado)">
                            {{ getEstadoTexto(linea.estado) }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </card>

            <card class="mt-4">
              <div class="p-3">
                <h5 class="mb-3"><i class="fas fa-truck mr-2 text-primary"></i>Desplazamientos</h5>
                <div v-if="!detalle.desplazamientos.length" class="alert alert-info mb-0">
                  Sin desplazamientos en SAT.
                </div>
                <ul v-else class="list-group list-group-flush">
                  <li
                    v-for="(d, idx) in detalle.desplazamientos"
                    :key="d.id || idx"
                    class="list-group-item px-0"
                  >
                    {{ d.descripcion || 'Desplazamiento' }} — {{ d.empleado || '—' }}
                  </li>
                </ul>
              </div>
            </card>
          </div>

          <div class="col-lg-6">
            <card>
              <div class="p-3">
                <h5 class="mb-3"><i class="fas fa-wrench mr-2 text-primary"></i>Mano de obra</h5>
                <div v-if="!detalle.mano_obra.length" class="alert alert-warning mb-0">
                  Sin líneas de mano de obra en SAT para este parte.
                </div>
                <div v-else>
                  <div
                    v-for="(mo, idx) in detalle.mano_obra"
                    :key="mo.id || idx"
                    class="mano-obra-item mb-3 pb-3 border-bottom"
                  >
                    <div class="font-weight-bold text-uppercase small text-muted">
                      {{ mo.clave }}
                    </div>
                    <div>{{ mo.descripcion || '—' }}</div>
                    <div class="small text-muted">
                      {{ formatDate(mo.fecha) }}
                      ({{ formatTime(mo.horaInicio) }} - {{ formatTime(mo.horaFin) }})
                      {{ mo.empleado }}
                    </div>
                    <div class="text-right font-weight-bold">{{ mo.cantidad }} hrs.</div>
                    <div v-if="mo.jefeEquipo" class="small">Jefe de equipo</div>
                    <div v-if="mo.dietaMedia || mo.dietaCompleta || mo.dietaMediaViaje" class="small text-muted">
                      Dietas:
                      <span v-if="mo.dietaMedia">Media</span>
                      <span v-if="mo.dietaCompleta"> · Completa</span>
                      <span v-if="mo.dietaMediaViaje"> · Media viaje</span>
                    </div>
                    <p v-if="mo.observaciones" class="small mb-0">{{ mo.observaciones }}</p>
                  </div>
                  <div class="text-right font-weight-bold">
                    Total: {{ detalle.total_horas_mano_obra }} hrs.
                  </div>
                </div>
              </div>
            </card>

            <card class="mt-4">
              <div class="p-3">
                <h5 class="mb-3"><i class="fas fa-clock mr-2 text-primary"></i>Fichajes FREDO (día del parte)</h5>
                <div v-if="!fredoEmpleados.length" class="alert alert-warning mb-0">
                  Sin fichajes FREDO para el empleado del parte ese día.
                </div>
                <div v-else>
                  <div
                    v-for="(emp, idx) in fredoEmpleados"
                    :key="idx"
                    class="mb-3"
                  >
                    <strong>{{ emp.empleado }}</strong>
                    <span v-if="emp.estado" class="text-muted ml-1">({{ emp.estado }})</span>
                    <div
                      v-for="(mov, midx) in emp.movimientos"
                      :key="midx"
                      class="small text-muted"
                    >
                      {{ formatDateTime(mov.fecha_hora) }} — {{ mov.label }}
                      <span v-if="mov.cliente"> · {{ mov.cliente }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </card>

            <card class="mt-4">
              <div class="p-3">
                <h5 class="mb-3"><i class="fas fa-signature mr-2 text-primary"></i>Firma</h5>
                <p><strong>Firma:</strong> {{ detalle.firma.firmante || 'No indicado' }}</p>
                <p><strong>DNI:</strong> {{ detalle.firma.dni || 'No indicado' }}</p>
                <p class="text-muted mb-0">
                  {{ detalle.firma.firmado ? 'Firmado' : 'No firmado' }}
                </p>
              </div>
            </card>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  displayParteField,
  formatParteDate,
  formatParteDateTime,
  formatParteTime,
  getParteNumero,
  getParteTitulo,
} from '@/util/parteSat';
import { getEstadoBadgeClass, getEstadoTexto } from '@/util/estadoColors';

export default {
  name: 'ViewPartePage',
  computed: {
    ...mapGetters('vistaOperativa', ['detail', 'loading', 'error']),
    parte() {
      return (this.detail && this.detail.parte_sat) || {};
    },
    detalle() {
      return (this.detail && this.detail.detalle) || {
        mano_obra: [],
        desplazamientos: [],
        material_albaran: { lineas: [] },
        firma: {},
        total_horas_mano_obra: 0,
      };
    },
    lineasMaterial() {
      const material =
        this.detalle.material_albaran ||
        (this.detail && this.detail.material_albaran) ||
        null;
      return (material && material.lineas) || [];
    },
    fredoEmpleados() {
      return (this.detail && this.detail.fredo_empleados) || [];
    },
    parteNumero() {
      return this.detail ? getParteNumero(this.detail) : '—';
    },
    titulo() {
      return this.detail ? getParteTitulo(this.detail) : '';
    },
  },
  mounted() {
    this.loadDetail(false);
  },
  methods: {
    async loadDetail(refresh = false) {
      try {
        await this.$store.dispatch('vistaOperativa/fetchDetail', {
          id: this.$route.params.id,
          refresh,
        });
      } catch (e) {
        this.$notify({
          type: 'danger',
          message: this.error || 'Error al cargar el parte',
        });
      }
    },
    goBack() {
      this.$router.push('/partes');
    },
    displayField: displayParteField,
    formatDate: formatParteDate,
    formatDateTime: formatParteDateTime,
    formatTime: formatParteTime,
    getEstadoBadgeClass,
    getEstadoTexto,
    getTipoLabel(tipo) {
      const labels = { activo: 'Activo', consumible: 'Consumible', saca: 'Saca' };
      return labels[tipo] || tipo;
    },
    getTipoBadgeClass(tipo) {
      const classes = {
        activo: 'badge-default',
        consumible: 'badge-warning',
        saca: 'badge-primary',
      };
      return classes[tipo] || 'badge-secondary';
    },
  },
};
</script>

<style scoped>
.mano-obra-item:last-child {
  border-bottom: none !important;
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}
</style>
